// CefrCycleWidget.tsx — Tira animada del ciclo "hablar es el motor" para el
// tope del Home, y el botón "?" del header. Ambos abren la guía (/help).
//
// El widget es ocultable con la "X" (se persiste en localStorage; no reaparece
// hasta que se limpie el storage). El ciclo se anima 100% en CSS y respeta
// prefers-reduced-motion (ver cefr-cycle.css).

import type { CSSProperties, ReactNode } from 'react';
import { Fragment, useState } from 'react';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { Sparkle } from '../doodles/Doodles.js';
import './cefr-cycle.css';

const DISMISS_KEY = 'orborix-home-cycle-widget-dismissed-v1';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // localStorage no disponible (modo privado / SSR): el widget se oculta
    // sólo en esta sesión, lo cual es aceptable.
  }
}

interface CycleStep {
  n: number;
  c: string;
  cap: string;
  icon: (c: string) => ReactNode;
}

const HCW_STEPS: ReadonlyArray<CycleStep> = [
  { n: 1, c: '#a78bfa', cap: 'Conversás de verdad',
    icon: (c) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={c} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"><path d="M21 12 a 9 9 0 0 1 -13 8 L 3 21 L 4 17 A 9 9 0 1 1 21 12 Z" /></svg> },
  { n: 2, c: '#fbbf24', cap: 'Te damos feedback',
    icon: (c) => <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 16 L 14 7 L 17 10 L 8 19 H 5 Z" /><path d="M13 8 L 16 11" /></svg> },
  { n: 3, c: '#5ce1e6', cap: 'Aprendemos cómo hablás',
    icon: (c) => <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="20" x2="5" y2="12" /><line x1="12" y1="20" x2="12" y2="5" /><line x1="19" y1="20" x2="19" y2="14" /></svg> },
  { n: 4, c: '#d4f23e', cap: 'El coach se adapta a vos',
    icon: (c) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={c} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.2" /><circle cx="12" cy="12" r="1.1" fill={c} stroke="none" /></svg> },
];

// Desfasajes de la animación CSS: cada nodo/leyenda se enciende en secuencia.
const DELAYS = ['0s', '1.9s', '3.8s', '5.7s', '7.6s'];

function LoopGlyph({ s }: { s: number }) {
  return (
    <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12 a 8 8 0 1 1 -2.3 -5.6" /><path d="M18 3 V 7 H 14" />
    </svg>
  );
}

/** Variables CSS custom no están en CSSProperties: helper para tiparlas. */
function cycleVars(c: string, delay: string): CSSProperties {
  return { '--hcw-c': c, '--hcw-delay': delay } as CSSProperties;
}

export function CefrCycleWidget() {
  const navigate = useViewNavigate();
  const [visible, setVisible] = useState(() => !isDismissed());

  if (!visible) return null;

  const open = () => navigate('/help');
  const dismiss = () => {
    persistDismiss();
    setVisible(false);
  };

  return (
    <div className="hcw" onClick={open} role="button" tabIndex={0} aria-label="Cómo mejorás hablando — ver guía">
      <div className="hcw-top">
        <span className="hcw-eyebrow"><Sparkle color="var(--lime)" size={12} /> ASÍ MEJORÁS, HABLANDO</span>
        <div className="hcw-top-right">
          <span className="hcw-hint">Ver guía <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6 L 15 12 L 9 18" /></svg></span>
          <button
            type="button"
            className="hcw-x"
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            aria-label="Ocultar este widget"
            title="Ocultar"
          >
            <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6 L 18 18 M18 6 L 6 18" /></svg>
          </button>
        </div>
      </div>

      <div className="hcw-rail">
        {HCW_STEPS.map((s, i) => (
          <Fragment key={`r-${s.n}`}>
            <span className="hcw-node" style={cycleVars(s.c, DELAYS[i] ?? '0s')}>
              <span className="hcw-node-step">{s.n}</span>
              {s.icon(s.c)}
            </span>
            {i < HCW_STEPS.length - 1 && <span className="hcw-conn" />}
          </Fragment>
        ))}
        <span className="hcw-loop" style={cycleVars('#a78bfa', DELAYS[4] ?? '7.6s')} title="…y volvés a empezar">
          <LoopGlyph s={15} />
        </span>
      </div>

      <div className="hcw-cap">
        {HCW_STEPS.map((s, i) => (
          <span key={`c-${s.n}`} className="hcw-cap-item" style={cycleVars(s.c, DELAYS[i] ?? '0s')}>
            <span className="n">{s.n}</span>{s.cap}
          </span>
        ))}
        <span className="hcw-cap-item hcw-cap-repeat" style={cycleVars('#a78bfa', DELAYS[4] ?? '7.6s')}>
          <span className="hcw-cap-loop"><LoopGlyph s={13} /></span>repeat.
        </span>
        <span className="hcw-cap-static">Conversás → feedback → aprendemos → el coach se adapta → repeat</span>
      </div>
    </div>
  );
}

/** Botón "?" del header del Home (a la derecha del saldo y la racha). */
export function HelpButton() {
  const navigate = useViewNavigate();
  return (
    <button
      type="button"
      className="hlp-help-btn"
      onClick={() => navigate('/help')}
      aria-label="Ayuda y guía"
      title="Ayuda"
    >
      <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9.2 9.2 a 2.8 2.8 0 0 1 5.3 1.2 c 0 1.8 -2.6 2.2 -2.6 4" /><path d="M12 17 h 0.01" /></svg>
    </button>
  );
}
